import { Injectable } from '@angular/core';
import { Division, IUnidadNegocio } from 'modelos/src';
import { Subject } from 'rxjs';
import { LoginService } from '../login/login.service';

export interface IFiltroGlobal {
  unidadNegocio?: IUnidadNegocio;
  division?: Division;
}

// El filtro global se guarda en localStorage keyeado por usuario
// (filtroGlobal.<userId>), igual que la config del mapa: si dos usuarios
// comparten el browser, la unidad de negocio de uno no aparece en la sesión del
// otro. Se persiste en localStorage y no en sessionStorage a propósito: el
// objetivo es que el filtro sobreviva a cerrar y abrir la pestaña.
const STORAGE_PREFIX = 'filtroGlobal';
function keyFiltroGlobal(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}`;
}

interface IFiltroGlobalGuardado {
  unidadNegocio?: IUnidadNegocio;
  division?: Division;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  public modoOscuro$ = new Subject<boolean>();

  public unidadNegocio$ = new Subject<IUnidadNegocio>();
  private unidadNegocio?: IUnidadNegocio;

  public division$ = new Subject<string>();
  private division?: Division;

  public filtro$ = new Subject<IFiltroGlobal>();

  // El filtro guardado se restaura una sola vez, de forma perezosa: el servicio
  // es `providedIn: 'root'` y puede construirse antes de que exista la sesión
  // (de la que sale la key del storage). Restaurar en el primer acceso real
  // garantiza que el primer consumidor ya lea el valor persistido, sin depender
  // del orden de inyección.
  private restaurado = false;

  constructor() {
    this.modoOscuro$.next(false);
    LoginService.sesionCerrada$.subscribe(() => this.reset());
  }

  public reset(): void {
    this.unidadNegocio$ = new Subject<IUnidadNegocio>();
    this.unidadNegocio = undefined;
    this.division$ = new Subject<string>();
    this.division = undefined;
    // El logout limpia el filtro en memoria pero NO el guardado: está keyeado
    // por usuario, así que volver a entrar con la misma cuenta recupera el
    // filtro, que es lo que se espera de una preferencia del navegador.
    this.restaurado = false;
  }

  public subscribeFiltroGlobal() {
    this.restaurar();
    return this.filtro$.asObservable();
  }
  private emitFiltroGlobal() {
    this.filtro$.next({
      unidadNegocio: this.unidadNegocio,
      division: this.division,
    });
  }
  public getFiltroGlobal(): IFiltroGlobal {
    this.restaurar();
    return {
      unidadNegocio: this.unidadNegocio,
      division: this.division,
    };
  }

  public setUnidadNegocio(unidadNegocio?: IUnidadNegocio) {
    this.restaurado = true;
    this.unidadNegocio = unidadNegocio;
    this.guardar();
    this.emitFiltroGlobal();
  }

  public setDivision(division?: Division) {
    this.restaurado = true;
    this.division = division;
    this.guardar();
    this.emitFiltroGlobal();
  }

  // ---------- Persistencia ----------

  // Id del usuario logueado. Sin sesión no hay dónde guardar: se devuelve
  // undefined y tanto guardar como restaurar quedan en no-op (en vez de
  // escribir en una key compartida tipo `filtroGlobal.anon`).
  private userId(): string | undefined {
    return LoginService.getUsuario()?._id;
  }

  private guardar(): void {
    const uid = this.userId();
    if (!uid) return;
    const key = keyFiltroGlobal(uid);
    if (!this.unidadNegocio && !this.division) {
      localStorage.removeItem(key);
      return;
    }
    const guardado: IFiltroGlobalGuardado = {
      // El listado del selector se pide con `select: 'nombre'`, así que la
      // opción elegida ya es {_id, nombre}: guardarla entera no arrastra datos
      // de más y permite pintar el selector antes de que llegue el listado.
      unidadNegocio: this.unidadNegocio
        ? { _id: this.unidadNegocio._id, nombre: this.unidadNegocio.nombre }
        : undefined,
      division: this.division,
    };
    localStorage.setItem(key, JSON.stringify(guardado));
  }

  private restaurar(): void {
    if (this.restaurado) return;
    const uid = this.userId();
    if (!uid) return; // Sin sesión todavía: se reintenta en el próximo acceso.
    this.restaurado = true;
    const raw = localStorage.getItem(keyFiltroGlobal(uid));
    if (!raw) return;
    try {
      const guardado: IFiltroGlobalGuardado = JSON.parse(raw);
      // Sin validar acá contra permisos ni contra el listado de unidades: eso lo
      // hace UsuarioComponent cuando tiene ambas cosas, y limpia lo que ya no
      // corresponda.
      if (guardado.unidadNegocio?._id) {
        this.unidadNegocio = guardado.unidadNegocio;
      }
      if (guardado.division) {
        this.division = guardado.division;
      }
    } catch {
      localStorage.removeItem(keyFiltroGlobal(uid));
    }
  }
}
